import { useEffect, useState } from "react";
import { Shield, ShieldCheck, Upload, Copy, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, Chip } from "@/components/common";

const INSURER_REQUEST = `Betreff: Anfrage zum Haftpflichtversicherungsschutz bei gelegentlicher entgeltlicher Wäschebearbeitung

Guten Tag,

ich bin bei Ihnen haftpflichtversichert und möchte mich über den Umfang meines Versicherungsschutzes informieren.

Ich beabsichtige, über die Vermittlungsplattform Washly gelegentlich gegen Bezahlung Wäsche anderer Privatpersonen zu waschen und zu trocknen. Die Wäschebearbeitung erfolgt in meinem privaten Haushalt mit einer haushaltsüblichen Waschmaschine beziehungsweise einem Trockner. Je nach Auftrag kann ich außerdem Wäsche zusammenlegen, bügeln sowie beim Kunden abholen und zurückbringen.

Ich möchte deshalb ausdrücklich wissen:
- Ist diese gelegentliche entgeltliche Tätigkeit im Rahmen meiner bestehenden Haftpflichtversicherung versichert?
- Sind Schäden an fremder Kleidung durch Waschen, Trocknen oder Bügeln eingeschlossen?
- Sind Bearbeitungsschäden wie Einlaufen oder Verfärbungen eingeschlossen?
- Besteht Deckung für Obhutsschäden sowie Verlust oder Verwechslung fremder Kleidung?
- Ist die Abholung und Rücklieferung der Kleidung mitversichert?
- Gibt es Umsatzgrenzen, Selbstbehalte, Deckungssummen oder besondere Ausschlüsse?
- Benötige ich einen zusätzlichen Versicherungsbaustein oder eine andere Police?

Ich würde mich über eine schriftliche Bestätigung freuen, aus der die konkret versicherten Tätigkeiten und Risiken hervorgehen.

Vielen Dank.
Mit freundlichen Grüßen`;

const STATUS_TONE = {
  none: ["neutral", Shield], pending: ["warn", Clock], rejected: ["error", XCircle],
  verified: ["mint", ShieldCheck], premium_pending: ["warn", Clock], premium: ["mint", ShieldCheck],
  expired: ["error", XCircle], withdrawn: ["error", XCircle],
};

function PolicyForm({ onDone }) {
  const [f, setF] = useState({ insurer: "", policyholder: "", policy_number: "", start_date: "", end_date: "", confirmation_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Bitte ein Dokument hochladen");
    if (!f.insurer || !f.policyholder || !f.policy_number) return toast.error("Bitte Pflichtfelder ausfüllen");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    try { await api.post("/provider/insurance/policy", fd); toast.success("Nachweis eingereicht – Prüfung läuft"); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form className="grid sm:grid-cols-2 gap-4" onSubmit={submit} data-testid="insurance-policy-form">
      <label className="block"><span className="w-label">Versicherungsgesellschaft *</span><input className="w-input" value={f.insurer} onChange={set("insurer")} data-testid="ins-insurer" /></label>
      <label className="block"><span className="w-label">Versicherungsnehmer *</span><input className="w-input" value={f.policyholder} onChange={set("policyholder")} data-testid="ins-policyholder" /></label>
      <label className="block"><span className="w-label">Versicherungsnummer *</span><input className="w-input" value={f.policy_number} onChange={set("policy_number")} data-testid="ins-policy-number" /></label>
      <label className="block"><span className="w-label">Datum der Bestätigung</span><input type="date" className="w-input" value={f.confirmation_date} onChange={set("confirmation_date")} data-testid="ins-confirmation-date" /></label>
      <label className="block"><span className="w-label">Vertragsbeginn</span><input type="date" className="w-input" value={f.start_date} onChange={set("start_date")} data-testid="ins-start-date" /></label>
      <label className="block"><span className="w-label">Vertragsende (falls vorhanden)</span><input type="date" className="w-input" value={f.end_date} onChange={set("end_date")} data-testid="ins-end-date" /></label>
      <label className="sm:col-span-2 w-btn w-btn-secondary cursor-pointer w-fit">
        <Upload size={18} aria-hidden="true" /> {file ? file.name : "Versicherungsdokument wählen (PDF, JPG, PNG)"}
        <input type="file" className="sr-only" accept="application/pdf,image/jpeg,image/png" data-testid="ins-policy-file"
          onChange={(e) => setFile(e.target.files[0] || null)} />
      </label>
      <button className="w-btn w-btn-primary sm:col-span-2 sm:w-fit" disabled={busy} data-testid="ins-policy-submit">{busy ? "Lädt hoch…" : "Nachweis einreichen"}</button>
    </form>
  );
}

function PremiumSection({ data, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const conf = data.confirmation;
  const copy = async () => { try { await navigator.clipboard.writeText(INSURER_REQUEST); toast.success("Anfrage kopiert"); } catch { toast.error("Konnte nicht kopieren"); } };
  const submit = async () => {
    if (!file) return toast.error("Bitte Antwort hochladen");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/provider/insurance/confirmation", fd); toast.success("Premium-Nachweis eingereicht"); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="w-card p-5 space-y-4" data-testid="insurance-premium-section">
      <div>
        <h3 className="text-lg font-extrabold">Wäschedeckung bestätigen lassen (optional)</h3>
        <p className="w-muted mt-1">Viele Haftpflichtversicherungen unterscheiden zwischen privaten Tätigkeiten und Dienstleistungen gegen Bezahlung. Deshalb empfehlen wir dir, bei deiner Versicherung schriftlich nachzufragen, ob das Waschen fremder Kleidung über Washly mitversichert ist.</p>
      </div>
      <div className="rounded-xl bg-mint-soft p-4 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid="insurer-request-text">{INSURER_REQUEST}</div>
      <button className="w-btn w-btn-secondary w-fit" onClick={copy} data-testid="ins-copy-request"><Copy size={16} aria-hidden="true" /> Anfrage an meine Versicherung kopieren</button>
      <div className="border-t border-line pt-4">
        {conf && conf.decision === "pending"
          ? <p className="font-semibold text-[var(--w-warn)] flex items-center gap-2" data-testid="ins-premium-pending"><Clock size={18} aria-hidden="true" /> Premium-Prüfung ausstehend</p>
          : (
            <div className="flex flex-wrap items-center gap-3">
              <label className="w-btn w-btn-secondary cursor-pointer w-fit">
                <Upload size={18} aria-hidden="true" /> {file ? file.name : "Antwort meiner Versicherung hochladen"}
                <input type="file" className="sr-only" accept="application/pdf,image/jpeg,image/png" data-testid="ins-confirmation-file"
                  onChange={(e) => setFile(e.target.files[0] || null)} />
              </label>
              <button className="w-btn w-btn-primary" onClick={submit} disabled={busy} data-testid="ins-confirmation-submit">{busy ? "Lädt hoch…" : "Einreichen"}</button>
            </div>
          )}
      </div>
    </div>
  );
}

export default function ProviderInsurance() {
  const [data, setData] = useState(null);
  const load = () => api.get("/provider/insurance").then((r) => setData(r.data)).catch(() => setData({ status: "none" }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  const [tone, Icon] = STATUS_TONE[data.status] || STATUS_TONE.none;
  const policy = data.policy;
  const canPremium = data.status === "verified" || data.status === "premium" || data.status === "premium_pending";
  return (
    <div className="space-y-6" data-testid="provider-insurance">
      <div className="w-card p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-xl font-extrabold">Deine Haftpflichtversicherung</h2>
          <Chip tone={tone} testId="ins-status-chip"><Icon size={16} aria-hidden="true" /> {data.status_label || "Noch kein Nachweis"}</Chip>
        </div>
        <p className="w-muted">Damit wir Vertrauen und Transparenz auf Washly stärken können, benötigen wir einen Nachweis über deine bestehende Haftpflichtversicherung. Unser Team prüft deine Angaben, bevor dein Versicherungsstatus freigeschaltet wird. Ein geprüfter Haftpflicht-Nachweis bestätigt noch keine Deckung für die entgeltliche Wäschebearbeitung.</p>
        {data.status === "rejected" && policy?.reject_reason && <p className="w-card p-3 bg-[var(--w-error-soft)] text-sm" data-testid="ins-reject-reason">Grund: {policy.reject_reason}</p>}
      </div>

      {data.status === "none" || data.status === "rejected" || data.status === "expired"
        ? <div className="w-card p-5"><PolicyForm onDone={load} /></div>
        : data.status === "pending"
          ? <p className="w-card p-5 font-semibold text-[var(--w-warn)] flex items-center gap-2" data-testid="ins-pending-note"><Clock size={18} aria-hidden="true" /> Dein Nachweis wird geprüft. Du wirst benachrichtigt, sobald die Prüfung abgeschlossen ist.</p>
          : null}

      {canPremium && <PremiumSection data={data} onDone={load} />}

      {data.status === "premium" && data.coverages && (
        <div className="w-card p-5" data-testid="ins-coverages">
          <h3 className="text-lg font-extrabold mb-2">Bestätigte Leistungen</h3>
          <ul className="space-y-1 text-sm">
            {Object.entries({ paid_processing: "Entgeltliches Waschen/Trocknen", wash_dry: "Waschen & Trocknen", ironing: "Bügeln", processing_damage: "Bearbeitungsschäden", custody_damage: "Obhutsschäden", loss: "Verlust", transport: "Abholung/Transport" }).map(([k, l]) => (
              <li key={k} className="flex items-center gap-2">{data.coverages[k] ? <CheckCircle2 size={14} className="text-mint-ink" aria-hidden="true" /> : <XCircle size={14} className="opacity-50" aria-hidden="true" />}<span className={data.coverages[k] ? "" : "opacity-60"}>{l}: {data.coverages[k] ? "bestätigt" : "nicht bestätigt"}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
